import { useCallback, type ReactNode } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import type { TFunction } from 'i18next';
import {
  useHtmlToReact,
  type ElementTransform,
} from '@/components/Common/HtmlToReact';
import {
  formatSapContractFeeAmount,
  getSapContractFeeHead,
  getSapContractFeeItems,
  groupSapContractFeeItems,
  SAP_CONTRACT_FEE_SUBTOTAL,
  type SapContractFeeData,
} from '@/utils/approval/sapContractFee';
import { SapContractFeeRoot } from './styles';

const HEAD_BG = '#ddebf7';
const LABEL_BG = '#f0f0f0';
const SUBTOTAL_BG = '#ffffe2';
const TOTAL_BG = '#fce4d6';

const bg = (color: string) => ({ backgroundColor: color });

type TableProps = {
  /** null 이면 레거시 초기 화면처럼 헤더만 */
  data: SapContractFeeData | null;
  t: TFunction;
};

/** 레거시 #contract_fee_table 마크업(칸 구성·색 동일) */
const SapContractFeeTable = ({ data, t }: TableProps) => {
  const items = getSapContractFeeItems(data);
  const head = getSapContractFeeHead(data);
  const groups = groupSapContractFeeItems(items);

  return (
    <table
      className="table table-orders table-bordered table-striped table-fixed no-margin text-center"
      id="contract_fee_table"
    >
      <thead>
        <tr>
          <th style={bg(HEAD_BG)} rowSpan={2}>
            {t('도급업체명')}
          </th>
          <th style={bg(HEAD_BG)} rowSpan={2}>
            {t('매장명')}
          </th>
          <th style={bg(HEAD_BG)} rowSpan={2}>
            {t('매출')}
          </th>
          <th style={bg(HEAD_BG)} rowSpan={2}>
            {t('매출수수료')}
          </th>
          <th style={bg(HEAD_BG)} rowSpan={2}>
            {t('본사지원계')}
          </th>
          <th style={bg(HEAD_BG)} rowSpan={2}>
            {t('공제전 수수료')}
          </th>
          <th style={bg(HEAD_BG)}>{t('수수료')}</th>
          <th style={bg(HEAD_BG)} colSpan={2}>
            {t('실지급액')}
          </th>
        </tr>
        <tr>
          <th style={bg(HEAD_BG)}>{t('매출대비')}</th>
          <th style={bg(HEAD_BG)}>{t('지급액')}</th>
          <th style={bg(HEAD_BG)}>{t('매출대비')}</th>
        </tr>
      </thead>
      <tbody id="contract_fee_item">
        {data && items.length === 0 && (
          <tr>
            <td colSpan={9}>{t('데이터가 없습니다.')}</td>
          </tr>
        )}
        {groups.map(([company, rows]) =>
          rows.map((row, index) => {
            const isSubtotal = row.SHOP_NM === SAP_CONTRACT_FEE_SUBTOTAL;
            const valueStyle = isSubtotal ? bg(SUBTOTAL_BG) : undefined;
            const labelStyle = bg(isSubtotal ? SUBTOTAL_BG : LABEL_BG);
            // 업체 첫 행은 레거시도 소계 색을 적용하지 않음
            const first = index === 0;
            return (
              <tr key={`${company}-${index}`}>
                {first && (
                  <th style={bg(LABEL_BG)} rowSpan={rows.length}>
                    {company}
                  </th>
                )}
                <th style={first ? bg(LABEL_BG) : labelStyle}>{row.SHOP_NM}</th>
                <th style={first ? undefined : valueStyle}>
                  {formatSapContractFeeAmount(row.ACTUALAMT)}
                </th>
                <th style={first ? undefined : valueStyle}>
                  {formatSapContractFeeAmount(row.ACTUSALESCOM)}
                </th>
                <th style={first ? undefined : valueStyle}>
                  {formatSapContractFeeAmount(row.TOTALSAMT)}
                </th>
                <th style={first ? undefined : valueStyle}>
                  {formatSapContractFeeAmount(row.TOTAL)}
                </th>
                <th style={first ? undefined : valueStyle}>{row.RATIO1}%</th>
                <th style={first ? bg(LABEL_BG) : labelStyle}>
                  {formatSapContractFeeAmount(row.REAL_AMT)}
                </th>
                <th style={first ? undefined : valueStyle}>{row.RATIO2}%</th>
              </tr>
            );
          }),
        )}
        {items.length > 0 && head && (
          <tr>
            <th style={bg(TOTAL_BG)} colSpan={2}>
              {t('합계')}
            </th>
            <th style={bg(TOTAL_BG)}>
              {formatSapContractFeeAmount(head.T_ACTUALAMT)}
            </th>
            <th style={bg(TOTAL_BG)}>
              {formatSapContractFeeAmount(head.T_ACTUSALESCOM)}
            </th>
            <th style={bg(TOTAL_BG)}>
              {formatSapContractFeeAmount(head.T_TOTALSAMT)}
            </th>
            <th style={bg(TOTAL_BG)}>
              {formatSapContractFeeAmount(head.T_TOTAL)}
            </th>
            <th style={bg(TOTAL_BG)}>{head.T_RATIO1}</th>
            <th style={bg(TOTAL_BG)}>
              {formatSapContractFeeAmount(head.T_REALAMT)}
            </th>
            <th style={bg(TOTAL_BG)}>{head.T_RATIO2}</th>
          </tr>
        )}
      </tbody>
    </table>
  );
};

/** 작성 화면 표 — 저장 content 와 같은 마크업 */
export const SapContractFeeTableView = (props: TableProps) => (
  <SapContractFeeRoot>
    <div className="form-group">
      <SapContractFeeTable {...props} />
    </div>
  </SapContractFeeRoot>
);

/** attributes.content — 레거시 조회 화면은 이 HTML 만 그대로 출력 */
export const buildSapContractFeeContentHtml = (
  data: SapContractFeeData,
  t: TFunction,
): string =>
  renderToStaticMarkup(
    <div className="form-group">
      <SapContractFeeTable data={data} t={t} />
    </div>,
  );

const DROP_TAGS = new Set(['style', 'script', 'link']);
// 레거시 content 의 줄바꿈 공백이 표 태그 바로 아래 남으면 React 경고
const TABLE_PARTS = new Set(['table', 'thead', 'tbody', 'tfoot', 'tr']);
const isBlankText = (node: ReactNode) =>
  typeof node === 'string' && !node.trim();

/** 저장 content(레거시 문서 포함) 그대로 */
export const SapContractFeeSnapshot = ({ content }: { content: string }) => {
  const transform = useCallback<ElementTransform>(
    ({ tagName, props, children }) => {
      if (DROP_TAGS.has(tagName)) return null;
      if (TABLE_PARTS.has(tagName)) {
        const kept = children.filter((c) => !isBlankText(c));
        children.splice(0, children.length, ...kept);
      }
      // 레거시 .form-group 의 overflow-x: scroll 은 스크롤바가 늘 보여 포탈 스크롤 래퍼로 대신함
      if (tagName === 'div' && props.style) {
        return (
          <div key={props.key as string} className={props.className as string}>
            {children}
          </div>
        );
      }
      return undefined;
    },
    [],
  );
  const rendered = useHtmlToReact(content, { transform });
  return <SapContractFeeRoot>{rendered}</SapContractFeeRoot>;
};
